import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatMoney, formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { canAccess } from '../../utils/rbac';

const COMPLEXITY_LEAD = { Simple: 1, Medium: 2, Complex: 3 };
const COMPLEXITY_OPTIONS = ['Simple', 'Medium', 'Complex'];

function urgencyMeta(sow) {
  const today = new Date().toISOString().split('T')[0];
  const todayMs = Date.parse(today);
  if (!sow.submission_deadline) {
    return { level: 'unscheduled', label: 'No deadline', color: 'text-gray-400', bg: 'bg-gray-50', dot: 'bg-gray-300', border: 'border-gray-200' };
  }
  const daysLeft = Math.round((Date.parse(sow.submission_deadline) - todayMs) / 86400000);
  const required = COMPLEXITY_LEAD[sow.draft_complexity] || 1;
  const buffer = daysLeft - required;
  if (daysLeft < 0) return { level: 'overdue', label: `${Math.abs(daysLeft)}d overdue`, color: 'text-red-700', bg: 'bg-red-50', dot: 'bg-red-500', border: 'border-red-200', daysLeft, buffer };
  if (buffer <= 0) return { level: 'critical', label: daysLeft === 0 ? 'Due TODAY' : `${daysLeft}d — starts now`, color: 'text-orange-700', bg: 'bg-orange-50', dot: 'bg-orange-500', border: 'border-orange-200', daysLeft, buffer };
  if (buffer <= 2) return { level: 'urgent', label: `${daysLeft}d left`, color: 'text-amber-700', bg: 'bg-amber-50', dot: 'bg-amber-400', border: 'border-amber-200', daysLeft, buffer };
  if (buffer <= 5) return { level: 'watch', label: `${daysLeft}d left`, color: 'text-yellow-700', bg: 'bg-yellow-50', dot: 'bg-yellow-400', border: 'border-yellow-200', daysLeft, buffer };
  return { level: 'good', label: `${daysLeft}d left`, color: 'text-emerald-700', bg: 'bg-emerald-50', dot: 'bg-emerald-400', border: 'border-emerald-100', daysLeft, buffer };
}

const URGENCY_ORDER = { overdue: 0, critical: 1, urgent: 2, watch: 3, unscheduled: 4, good: 5 };

function StatCard({ label, value, sub, accent }) {
  const accentMap = {
    red:   { border: 'border-red-400', bg: 'bg-red-50', text: 'text-red-700', val: 'text-red-600' },
    amber: { border: 'border-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', val: 'text-amber-600' },
    gray:  { border: 'border-gray-300', bg: 'bg-gray-50', text: 'text-gray-500', val: 'text-gray-700' },
    gold:  { border: 'border-yellow-400', bg: 'bg-yellow-50', text: 'text-yellow-700', val: 'text-yellow-700' },
    navy:  { border: 'border-navy', bg: 'bg-blue-50', text: 'text-navy', val: 'text-navy' },
  };
  const c = accentMap[accent] || accentMap.navy;
  return (
    <div className={`rounded-lg border-l-4 ${c.border} ${c.bg} px-4 py-3 flex flex-col gap-0.5 min-w-0`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${c.text}`}>{label}</div>
      <div className={`text-2xl font-bold leading-tight ${c.val}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

function ComplexityBadge({ value }) {
  if (!value) return <span className="text-gray-300 text-[10px]">—</span>;
  const map = { Simple: 'bg-emerald-100 text-emerald-700', Medium: 'bg-amber-100 text-amber-700', Complex: 'bg-red-100 text-red-700' };
  return <span className={`px-1.5 py-px rounded text-[9px] font-bold ${map[value] || 'bg-gray-100 text-gray-600'}`}>{value}</span>;
}

function UrgencyBadge({ meta }) {
  const levelLabel = { overdue: 'OVERDUE', critical: 'CRITICAL', urgent: 'URGENT', watch: 'WATCH', good: 'ON TRACK', unscheduled: 'NO DATE' };
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
      <span className={`text-[9px] font-bold uppercase tracking-wide ${meta.color}`}>{levelLabel[meta.level]}</span>
    </div>
  );
}

function InlineSelect({ value, options, onChange, placeholder = '—' }) {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-left hover:bg-gray-100 rounded px-1 -mx-1 transition-colors min-w-[60px]"
      >
        {value || <span className="text-gray-300">{placeholder}</span>}
      </button>
    );
  }
  return (
    <select
      autoFocus
      className="text-[11px] border border-gold rounded px-1 py-0.5 bg-white"
      value={value || ''}
      onChange={e => { onChange(e.target.value || null); setEditing(false); }}
      onBlur={() => setEditing(false)}
    >
      <option value="">— clear —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function InlineDate({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-left hover:bg-gray-100 rounded px-1 -mx-1 transition-colors whitespace-nowrap"
      >
        {value ? formatDate(value) : <span className="text-gray-300">Set date…</span>}
      </button>
    );
  }
  return (
    <input
      type="date"
      autoFocus
      className="text-[11px] border border-gold rounded px-1 py-0.5 bg-white"
      defaultValue={value || ''}
      onBlur={e => { onChange(e.target.value || null); setEditing(false); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { onChange(e.target.value || null); setEditing(false); }
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

function InlineNumber({ value, onChange, suffix = '%' }) {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-left hover:bg-gray-100 rounded px-1 -mx-1 transition-colors"
      >
        {value != null ? `${value}${suffix}` : <span className="text-gray-300">—</span>}
      </button>
    );
  }
  return (
    <input
      type="number"
      autoFocus
      min={0}
      max={100}
      className="text-[11px] border border-gold rounded px-1 py-0.5 bg-white w-16"
      defaultValue={value ?? ''}
      onBlur={e => { const v = e.target.value === '' ? null : parseInt(e.target.value); onChange(v); setEditing(false); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { const v = e.target.value === '' ? null : parseInt(e.target.value); onChange(v); setEditing(false); }
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

export default function DraftSowDashboard() {
  const { navigate } = useApp();
  // Draft values are SOW money, the sow:see-financials grant; the API leaves them
  // out for anybody else (pen test AD26341, AD12/AD16), so neither is the column.
  const seesMoney = canAccess('sow:see-financials');
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filterUrgency, setFilterUrgency] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    api.get('sows/draft-dashboard')
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { addToast('Failed to load draft SOW dashboard', 'error'); setLoading(false); });
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);

  const saveField = useCallback(async (id, field, value) => {
    setSavingId(id);
    try {
      await api.put(`sows/${id}`, { [field]: value });
      setData(prev => {
        if (!prev) return prev;
        const updated = prev.sows.map(s => s.id === id ? { ...s, [field]: value } : s);
        const today = new Date().toISOString().split('T')[0];
        const todayMs = Date.parse(today);
        const leadDays = { Simple: 1, Medium: 2, Complex: 3 };
        const enriched = updated.map(s => {
          let days_left = null, buffer = null, urgency = 'unscheduled';
          if (s.submission_deadline) {
            days_left = Math.round((Date.parse(s.submission_deadline) - todayMs) / 86400000);
            const required = leadDays[s.draft_complexity] || 1;
            buffer = days_left - required;
            if (days_left < 0) urgency = 'overdue';
            else if (buffer <= 0) urgency = 'critical';
            else if (buffer <= 2) urgency = 'urgent';
            else if (buffer <= 5) urgency = 'watch';
            else urgency = 'good';
          }
          return { ...s, days_left, buffer, urgency };
        });
        enriched.sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 9) - (URGENCY_ORDER[b.urgency] ?? 9) || (a.days_left ?? 9999) - (b.days_left ?? 9999));
        const stats = {
          total: enriched.length,
          overdue: enriched.filter(s => s.urgency === 'overdue').length,
          due_this_week: enriched.filter(s => s.days_left !== null && s.days_left >= 0 && s.days_left <= 7).length,
          no_deadline: enriched.filter(s => !s.submission_deadline).length,
          pipeline_value: enriched.reduce((sum, s) => sum + (s.total_excl_vat || 0), 0),
        };
        return { stats, sows: enriched };
      });
    } catch {
      addToast('Failed to save change', 'error');
    } finally {
      setSavingId(null);
    }
  }, [addToast]);

  const visibleSows = data?.sows?.filter(s => {
    if (filterUrgency === 'all') return true;
    const m = urgencyMeta(s);
    return m.level === filterUrgency;
  }) ?? [];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-gray-400">Loading…</div>
      </div>
    );
  }

  const stats = data?.stats || {};

  const URGENCY_FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'critical', label: 'Critical' },
    { key: 'urgent', label: 'Urgent' },
    { key: 'watch', label: 'Watch' },
    { key: 'unscheduled', label: 'No Date' },
    { key: 'good', label: 'On Track' },
  ];

  return (
    <div className="p-4 max-w-full">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-lg font-bold text-navy">Draft SOW Dashboard</h1>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Deadline tracking, complexity ratings, and urgency scoring for all outstanding draft SOWs.
          Click any cell to edit inline. Emails fire automatically each weekday morning.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-5">
        <StatCard
          label="Total Drafts"
          value={stats.total ?? 0}
          sub="outstanding"
          accent="navy"
        />
        <StatCard
          label="Overdue"
          value={stats.overdue ?? 0}
          sub={stats.overdue ? 'deadline passed' : 'all good'}
          accent={stats.overdue ? 'red' : 'gray'}
        />
        <StatCard
          label="Due This Week"
          value={stats.due_this_week ?? 0}
          sub="next 7 days"
          accent={(stats.due_this_week ?? 0) > 0 ? 'amber' : 'gray'}
        />
        <StatCard
          label="No Deadline"
          value={stats.no_deadline ?? 0}
          sub="needs scheduling"
          accent={(stats.no_deadline ?? 0) > 0 ? 'amber' : 'gray'}
        />
        {seesMoney && (
        <StatCard
          label="Pipeline Value"
          value={formatMoney(stats.pipeline_value ?? 0)}
          sub="excl. VAT"
          accent="gold"
        />
        )}
      </div>

      {/* Urgency legend */}
      <div className="flex flex-wrap items-center gap-3 mb-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Urgency Guide:</span>
        {[
          { dot: 'bg-red-500', label: 'Overdue — deadline passed' },
          { dot: 'bg-orange-500', label: 'Critical — must start now (lead time ≤ deadline)' },
          { dot: 'bg-amber-400', label: 'Urgent — starts in ≤2 days' },
          { dot: 'bg-yellow-400', label: 'Watch — starts in ≤5 days' },
          { dot: 'bg-emerald-400', label: 'On Track' },
          { dot: 'bg-gray-300', label: 'No deadline set' },
        ].map(({ dot, label }) => (
          <div key={label} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${dot}`} />
            <span className="text-[10px] text-gray-500">{label}</span>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-3 flex-wrap">
        {URGENCY_FILTER_TABS.map(tab => {
          const counts = {
            all: data?.sows?.length ?? 0,
            overdue: data?.sows?.filter(s => urgencyMeta(s).level === 'overdue').length ?? 0,
            critical: data?.sows?.filter(s => urgencyMeta(s).level === 'critical').length ?? 0,
            urgent: data?.sows?.filter(s => urgencyMeta(s).level === 'urgent').length ?? 0,
            watch: data?.sows?.filter(s => urgencyMeta(s).level === 'watch').length ?? 0,
            unscheduled: data?.sows?.filter(s => urgencyMeta(s).level === 'unscheduled').length ?? 0,
            good: data?.sows?.filter(s => urgencyMeta(s).level === 'good').length ?? 0,
          };
          const isActive = filterUrgency === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setFilterUrgency(tab.key)}
              className={`px-2.5 py-1 rounded text-[10px] font-semibold border transition-colors ${
                isActive
                  ? 'bg-navy text-white border-navy'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
              }`}
            >
              {tab.label}
              {counts[tab.key] > 0 && (
                <span className={`ml-1.5 text-[9px] px-1 rounded-full ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>
                  {counts[tab.key]}
                </span>
              )}
            </button>
          );
        })}
        <div className="flex-1" />
        <button
          onClick={load}
          className="px-2.5 py-1 rounded text-[10px] font-semibold border border-gray-200 bg-white text-gray-500 hover:border-gray-400 transition-colors"
        >
          ↺ Refresh
        </button>
      </div>

      {/* Table */}
      {visibleSows.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
          {filterUrgency === 'all'
            ? 'No draft SOWs found — all clear!'
            : `No ${filterUrgency} draft SOWs.`}
        </div>
      ) : (
        <>
        {/* Phone: cards. A ten-column table at 390px scrolls sideways, which
            pushes the reference column (the one you navigate by) off screen and
            truncates every other cell to an ellipsis. Cards show the whole
            record instead. */}
        <div className="md:hidden space-y-2">
          {visibleSows.map(sow => {
            const meta = urgencyMeta(sow);
            const isOverdue = meta.level === 'overdue';
            return (
              <div key={sow.id}
                className={`rounded-lg border border-gray-200 bg-white p-3 shadow-sm ${savingId === sow.id ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <button
                    onClick={() => navigate('sow-detail', sow.id)}
                    className="font-mono text-[13px] font-bold text-navy hover:text-gold transition-colors text-left"
                  >
                    {sow.reference}
                  </button>
                  <UrgencyBadge meta={meta} />
                </div>

                <div className="text-[13px] font-semibold text-gray-800 mt-1.5">{sow.client_name || '—'}</div>
                {sow.detail && <div className="text-[12px] text-gray-500 mt-0.5">{sow.detail}</div>}

                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-2.5 pt-2.5 border-t border-gray-100">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Owner</div>
                    <div className="text-[12px] text-gray-700">{sow.owner_name || '—'}</div>
                  </div>
                  {seesMoney && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Value</div>
                    <div className="text-[12px] text-gray-700 font-medium">
                      {sow.total_excl_vat ? formatMoney(sow.total_excl_vat) : '—'}
                    </div>
                  </div>
                  )}
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Deadline</div>
                    <div className={`text-[12px] ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                      <InlineDate
                        value={sow.submission_deadline}
                        onChange={v => saveField(sow.id, 'submission_deadline', v)}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Days left</div>
                    <div className={`text-[12px] font-semibold ${meta.color}`}>{meta.label}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Complexity</div>
                    <div className="flex items-center gap-1.5">
                      <ComplexityBadge value={sow.draft_complexity} />
                      <InlineSelect
                        value={sow.draft_complexity}
                        options={COMPLEXITY_OPTIONS}
                        onChange={v => saveField(sow.id, 'draft_complexity', v)}
                        placeholder="set…"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Win %</div>
                    <div className="text-[12px]">
                      <InlineNumber
                        value={sow.win_probability}
                        onChange={v => saveField(sow.id, 'win_probability', v)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide whitespace-nowrap">Urgency</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide">Reference</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide">Client</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide">Detail</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide">Owner</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide whitespace-nowrap">
                  Deadline
                  <span className="ml-1 text-gold font-normal normal-case tracking-normal">✎</span>
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide whitespace-nowrap">Days Left</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-[9px] uppercase tracking-wide whitespace-nowrap">
                  Complexity
                  <span className="ml-1 text-gold font-normal normal-case tracking-normal">✎</span>
                </th>
                <th className="px-3 py-2 text-right font-semibold text-gray-500 text-[9px] uppercase tracking-wide whitespace-nowrap">
                  Win %
                  <span className="ml-1 text-gold font-normal normal-case tracking-normal">✎</span>
                </th>
                {seesMoney && <th className="px-3 py-2 text-right font-semibold text-gray-500 text-[9px] uppercase tracking-wide whitespace-nowrap">Value</th>}
              </tr>
            </thead>
            <tbody>
              {visibleSows.map((sow, idx) => {
                const meta = urgencyMeta(sow);
                const isSaving = savingId === sow.id;
                const isOverdue = meta.level === 'overdue';
                const isCritical = meta.level === 'critical';
                return (
                  <tr
                    key={sow.id}
                    className={`border-b border-gray-100 transition-colors hover:bg-gray-50 ${
                      isSaving ? 'opacity-60' : ''
                    } ${idx % 2 === 0 ? '' : 'bg-gray-50'}`}
                  >
                    {/* Urgency */}
                    <td className={`px-3 py-2.5 ${meta.bg}`}>
                      <UrgencyBadge meta={meta} />
                    </td>

                    {/* Reference */}
                    <td className="px-3 py-2.5 font-semibold">
                      <button
                        onClick={() => navigate('sow-detail', sow.id)}
                        className="text-navy hover:text-gold transition-colors text-left font-mono text-[10px]"
                      >
                        {sow.reference}
                      </button>
                    </td>

                    {/* Client */}
                    <td className="px-3 py-2.5 text-gray-600 max-w-[120px] truncate">
                      {sow.client_name || '—'}
                    </td>

                    {/* Detail */}
                    <td className="px-3 py-2.5 text-gray-500 max-w-[200px]">
                      <span className="line-clamp-1" title={sow.detail || ''}>
                        {sow.detail || <span className="text-gray-300">No description</span>}
                      </span>
                    </td>

                    {/* Owner */}
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {sow.owner_name || '—'}
                    </td>

                    {/* Deadline — inline edit */}
                    <td className={`px-3 py-2.5 ${isOverdue ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                      <InlineDate
                        value={sow.submission_deadline}
                        onChange={v => saveField(sow.id, 'submission_deadline', v)}
                      />
                    </td>

                    {/* Days left */}
                    <td className={`px-3 py-2.5 font-semibold ${meta.color}`}>
                      {meta.label}
                    </td>

                    {/* Complexity — inline edit */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <ComplexityBadge value={sow.draft_complexity} />
                        <InlineSelect
                          value={sow.draft_complexity}
                          options={COMPLEXITY_OPTIONS}
                          onChange={v => saveField(sow.id, 'draft_complexity', v)}
                          placeholder="set…"
                        />
                      </div>
                    </td>

                    {/* Win % — inline edit */}
                    <td className="px-3 py-2.5 text-right">
                      <InlineNumber
                        value={sow.win_probability}
                        onChange={v => saveField(sow.id, 'win_probability', v)}
                      />
                    </td>

                    {/* Value */}
                    {seesMoney && <td className="px-3 py-2.5 text-right text-gray-700 font-medium whitespace-nowrap">
                      {sow.total_excl_vat ? formatMoney(sow.total_excl_vat) : <span className="text-gray-300">—</span>}
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Email schedule reminder */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-2">
        <svg className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
        <div>
          <span className="text-[10px] font-semibold text-blue-700">Automated email reminders</span>
          <span className="text-[10px] text-blue-500 ml-1">
            — owners receive emails at 8:30am on weekdays: when a deadline is 7 days away (heads up), 3 days away (reminder),
            due today or tomorrow (critical), past deadline (Mon/Wed/Fri until resolved), and
            every Monday for SOWs with no deadline set.
          </span>
        </div>
      </div>
    </div>
  );
}
